import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { FiSave, FiPlus, FiTrash2 } from 'react-icons/fi'
import { useContent } from '../../../hooks/useContent'
import { FooterContent, SocialLink, NavigationItem } from '../../../types/content'
import toast from 'react-hot-toast'

const FooterEditor: React.FC = () => {
  const { content, loading, saving, saveContent } = useContent('footer')
  const [formData, setFormData] = useState<FooterContent>({
    logo: '',
    logoAlt: '',
    companyDescription: '',
    contactInfo: {
      email: [],
      phone: [],
      address: [],
      hours: []
    },
    socialLinks: [],
    quickLinks: [],
    copyrightText: ''
  })

  useEffect(() => {
    if (content?.data) {
      const footerData = content.data as FooterContent
      setFormData(footerData)
    }
  }, [content])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const success = await saveContent(formData)
    if (success) {
      toast.success('Footer updated successfully!')
    }
  }

  const addSocialLink = () => {
    const newLink: SocialLink = {
      platform: 'Instagram',
      url: '',
      icon: 'FiInstagram'
    }
    setFormData({
      ...formData,
      socialLinks: [...formData.socialLinks, newLink]
    })
  }

  const updateSocialLink = (index: number, field: keyof SocialLink, value: string) => {
    const updatedLinks = formData.socialLinks.map((link, i) => 
      i === index ? { ...link, [field]: value } : link
    )
    setFormData({
      ...formData,
      socialLinks: updatedLinks
    })
  }

  const removeSocialLink = (index: number) => {
    const updatedLinks = formData.socialLinks.filter((_, i) => i !== index)
    setFormData({
      ...formData,
      socialLinks: updatedLinks
    })
  }

  const addQuickLink = () => {
    const newLink: NavigationItem = {
      name: 'New Link',
      href: '#new',
      order: formData.quickLinks.length + 1
    }
    setFormData({
      ...formData,
      quickLinks: [...formData.quickLinks, newLink]
    })
  }

  const updateQuickLink = (index: number, field: keyof NavigationItem, value: string | number) => {
    const updatedLinks = formData.quickLinks.map((link, i) => 
      i === index ? { ...link, [field]: value } : link
    )
    setFormData({
      ...formData,
      quickLinks: updatedLinks
    })
  }

  const removeQuickLink = (index: number) => {
    const updatedLinks = formData.quickLinks.filter((_, i) => i !== index)
    setFormData({
      ...formData,
      quickLinks: updatedLinks
    })
  }

  const updateContactInfo = (field: keyof FooterContent['contactInfo'], index: number, value: string) => {
    const updatedContactInfo = { ...formData.contactInfo }
    const updatedArray = [...updatedContactInfo[field]]
    updatedArray[index] = value
    updatedContactInfo[field] = updatedArray.filter(item => item.trim() !== '')
    
    setFormData({
      ...formData,
      contactInfo: updatedContactInfo
    })
  }

  const addContactInfo = (field: keyof FooterContent['contactInfo']) => {
    const updatedContactInfo = { ...formData.contactInfo }
    updatedContactInfo[field] = [...updatedContactInfo[field], '']
    
    setFormData({
      ...formData,
      contactInfo: updatedContactInfo
    })
  }

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-neutral-200 rounded w-1/4"></div>
          <div className="h-4 bg-neutral-200 rounded w-3/4"></div>
          <div className="space-y-2">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={i} className="h-10 bg-neutral-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  const socialPlatforms = [
    { value: 'Instagram', icon: 'FiInstagram' },
    { value: 'Twitter', icon: 'FiTwitter' },
    { value: 'LinkedIn', icon: 'FiLinkedin' },
    { value: 'Facebook', icon: 'FiFacebook' }
  ]

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-neutral-800">Footer Settings</h2>
          <p className="text-neutral-600 text-sm mt-1">
            Configure the site footer content, contact information, and links.
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Logo & Company Info */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-neutral-800">Company Information</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Logo URL
              </label>
              <input
                type="text"
                value={formData.logo}
                onChange={(e) => setFormData({ ...formData, logo: e.target.value })}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="/assets/logo.png"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Logo Alt Text
              </label>
              <input
                type="text"
                value={formData.logoAlt}
                onChange={(e) => setFormData({ ...formData, logoAlt: e.target.value })}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Company Name"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Company Description
            </label>
            <textarea
              value={formData.companyDescription}
              onChange={(e) => setFormData({ ...formData, companyDescription: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="Brief description of your company..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Copyright Text
            </label>
            <input
              type="text"
              value={formData.copyrightText}
              onChange={(e) => setFormData({ ...formData, copyrightText: e.target.value })}
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              placeholder="© 2024 Company Name. All rights reserved."
            />
          </div>
        </div>

        {/* Contact Information */}
        <div className="space-y-4">
          <h3 className="text-lg font-medium text-neutral-800">Contact Information</h3>
          
          {/* Email */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-neutral-700">
                Email Addresses
              </label>
              <button
                type="button"
                onClick={() => addContactInfo('email')}
                className="text-sm text-primary hover:text-primary/80"
              >
                + Add Email
              </button>
            </div>
            <div className="space-y-2">
              {formData.contactInfo.email.map((email, index) => (
                <input
                  key={index}
                  type="email"
                  value={email}
                  onChange={(e) => updateContactInfo('email', index, e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="contact@company.com"
                />
              ))}
            </div>
          </div>

          {/* Phone */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-neutral-700">
                Phone Numbers
              </label>
              <button
                type="button"
                onClick={() => addContactInfo('phone')}
                className="text-sm text-primary hover:text-primary/80"
              >
                + Add Phone
              </button>
            </div>
            <div className="space-y-2">
              {formData.contactInfo.phone.map((phone, index) => (
                <input
                  key={index}
                  type="tel"
                  value={phone}
                  onChange={(e) => updateContactInfo('phone', index, e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="+1 (555) 123-4567"
                />
              ))}
            </div>
          </div>

          {/* Address */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-neutral-700">
                Address Lines
              </label>
              <button
                type="button"
                onClick={() => addContactInfo('address')}
                className="text-sm text-primary hover:text-primary/80"
              >
                + Add Address
              </button>
            </div>
            <div className="space-y-2">
              {formData.contactInfo.address.map((address, index) => (
                <input
                  key={index}
                  type="text"
                  value={address}
                  onChange={(e) => updateContactInfo('address', index, e.target.value)}
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="123 Main St, City, State 12345"
                />
              ))}
            </div>
          </div>
        </div>

        {/* Social Links */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-neutral-800">Social Media</h3>
            <button
              type="button"
              onClick={addSocialLink}
              className="flex items-center space-x-2 px-3 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
            >
              <FiPlus size={16} />
              <span>Add Social Link</span>
            </button>
          </div>

          <div className="space-y-3">
            {formData.socialLinks.map((link, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-4 border border-neutral-200 rounded-lg"
              >
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Platform
                    </label>
                    <select
                      value={link.platform}
                      onChange={(e) => {
                        const platform = socialPlatforms.find(p => p.value === e.target.value)
                        updateSocialLink(index, 'platform', e.target.value)
                        if (platform) {
                          updateSocialLink(index, 'icon', platform.icon)
                        }
                      }}
                      className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                    >
                      {socialPlatforms.map((platform) => (
                        <option key={platform.value} value={platform.value}>
                          {platform.value}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      URL
                    </label>
                    <input
                      type="url"
                      value={link.url}
                      onChange={(e) => updateSocialLink(index, 'url', e.target.value)}
                      className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="https://instagram.com/username"
                    />
                  </div>
                  
                  <div className="flex items-end">
                    <button
                      type="button"
                      onClick={() => removeSocialLink(index)}
                      className="w-full px-3 py-2 text-red-600 border border-red-300 rounded-lg hover:bg-red-50 transition-colors"
                    >
                      <FiTrash2 size={16} className="mx-auto" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Quick Links */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-neutral-800">Quick Links</h3>
            <button
              type="button"
              onClick={addQuickLink}
              className="flex items-center space-x-2 px-3 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
            >
              <FiPlus size={16} />
              <span>Add Link</span>
            </button>
          </div>

          <div className="space-y-3">
            {formData.quickLinks.map((link, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-4 border border-neutral-200 rounded-lg"
              >
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Name
                    </label>
                    <input
                      type="text"
                      value={link.name}
                      onChange={(e) => updateQuickLink(index, 'name', e.target.value)}
                      className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="About Us"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">
                      Link (href)
                    </label>
                    <input
                      type="text"
                      value={link.href}
                      onChange={(e) => updateQuickLink(index, 'href', e.target.value)}
                      className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                      placeholder="#about"
                    />
                  </div>
                  
                  <div className="flex items-end">
                    <button
                      type="button"
                      onClick={() => removeQuickLink(index)}
                      className="w-full px-3 py-2 text-red-600 border border-red-300 rounded-lg hover:bg-red-50 transition-colors"
                    >
                      <FiTrash2 size={16} className="mx-auto" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end pt-6 border-t border-neutral-200">
          <button
            type="submit"
            disabled={saving}
            className="flex items-center space-x-2 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <FiSave size={16} />
            <span>{saving ? 'Saving...' : 'Save Changes'}</span>
          </button>
        </div>
      </form>
    </div>
  )
}

export default FooterEditor
